import { GroupsOutlined as GroupsOutlinedIcon } from 'components/icons';
import PermissionButton from 'components/buttons/PermissionButton';
import EmptyState from 'components/EmptyState';
import { DataTable, useDataTableSortString } from 'components/tables/data-table';
import type { DataTableColumnDef } from 'components/tables/data-table';
import { sortByAccessor } from 'components/tables/list-table/sortUtil';
import { useRowLink } from 'components/tables/list-table/useRowLink';
import React, { useCallback, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Link as RouterLink } from 'react-router-dom';
import { EmployeeMinDtoWithEmployment } from 'types/employees';
import { formatDateStringInput } from 'utils/formatDateStringInput';
import { t } from 'utils/getTranslations';
import { useNavigateWithReturnState } from 'utils/useNavigateWithReturnState';

interface Props {
    jobRoleEmployees: EmployeeMinDtoWithEmployment[];
}

const DEFAULT_SORT = 'name,asc';

const getEmployeeHref = (employee: EmployeeMinDtoWithEmployment) => `/employee/${employee.id}`;

const getValidToLabel = (employee: EmployeeMinDtoWithEmployment): string => {
    const validTo = employee.latestEmployment?.validTo;
    if (!validTo) return '-';
    return formatDateStringInput(new Date(validTo)) || '-';
};

// Accessor used for the client-side sort comparator, keyed by column id so a header click drives
// the in-memory sort below. The valid-to accessor returns the raw timestamp for chronological sort.
const SORT_ACCESSORS: Record<string, (employee: EmployeeMinDtoWithEmployment) => number | string | null> = {
    name: (employee) => employee.name || '-',
    'employment-valid-to': (employee) => employee.latestEmployment?.validTo ?? null,
};

const buildColumns = (
    navigateWithReturn: ReturnType<typeof useNavigateWithReturnState>
): DataTableColumnDef<EmployeeMinDtoWithEmployment>[] => [
    {
        header: t('employees.employee'),
        id: 'name',
        accessorFn: (row) => row.name || '-',
        meta: {
            widthWeight: 1,
        },
        cell: ({ row }) => (
            <RouterLink
                className="entity-link font-semibold hover:underline"
                to={`/employee/${row.original.id}`}
                onClick={(event) => {
                    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) {
                        return;
                    }
                    event.preventDefault();
                    navigateWithReturn(`/employee/${row.original.id}`);
                }}
            >
                {row.original.name || '-'}
            </RouterLink>
        ),
    },
    {
        header: t('employees.employment.form.valid-to'),
        id: 'employment-valid-to',
        accessorFn: (row) => getValidToLabel(row),
        meta: {
            widthWeight: 1,
        },
    },
];

const EmployeesView: React.FC<Props> = ({ jobRoleEmployees }) => {
    const navigateWithReturn = useNavigateWithReturnState();
    const rowLink = useRowLink(getEmployeeHref);
    const [sort, setSort] = useState(DEFAULT_SORT);

    const columns = useMemo<DataTableColumnDef<EmployeeMinDtoWithEmployment>[]>(
        () => buildColumns(navigateWithReturn),
        [navigateWithReturn]
    );

    // The table runs in manual-sort mode, so it never reorders rows itself — it only emits the
    // requested sort. We feed it pre-sorted in-memory data (already returned by GET /job-role/:id).
    const sortedEmployees = useMemo(() => {
        const [accessorId, direction] = sort.split(',');
        const accessor =
            SORT_ACCESSORS[accessorId] ?? ((employee: EmployeeMinDtoWithEmployment) => employee.name || '-');
        return sortByAccessor(jobRoleEmployees, accessor, direction === 'desc');
    }, [jobRoleEmployees, sort]);

    const applySort = useCallback((newSort: string) => setSort(newSort || DEFAULT_SORT), []);
    const { sorting, onSortingChange } = useDataTableSortString(sort, applySort, columns);

    return (
        <div className="flex h-full flex-col gap-4">
            <DataTable<EmployeeMinDtoWithEmployment>
                columns={columns}
                data={sortedEmployees}
                {...rowLink}
                columnConfigId="job-role-employees"
                sorting={sorting}
                onSortingChange={onSortingChange}
                // This page has no create action — a job role is assigned from the colleague's own
                // profile — so the CTA is navigational.
                emptyState={
                    <EmptyState
                        size="page"
                        icon={GroupsOutlinedIcon}
                        titleId="job-roles.empty.employees-title"
                        bodyId="job-roles.empty.employees-body"
                        surface="job-role-employees"
                        action={
                            <PermissionButton
                                permissions={['READ.EMPLOYEES']}
                                variant="contained"
                                color="secondary"
                                onClick={() => navigateWithReturn('/employees')}
                            >
                                <FormattedMessage id="employees.go-to-colleagues" />
                            </PermissionButton>
                        }
                    />
                }
            />
        </div>
    );
};

export default EmployeesView;
